import { GlobalCacheConfig, ICachePair, IStorageStrategy, LocalStorageStrategy } from 'ts-cacheable';

/**
 * LocalStorageStrategy wrapper that never lets a storage failure escape into the cached
 * observable stream. ts-cacheable persists entries inside the response pipeline, so a plain
 * `localStorage.setItem` quota overflow errors the response itself and breaks the calling
 * screen (RCRM: territories/add spinning forever on 'CACHE_STORAGE' exceeded the quota).
 * All cached entries are refetchable nomenclatures, so on failure the whole cache is dropped
 * and the write retried once; if the entry alone exceeds the quota it is served uncached.
 */
class QuotaSafeLocalStorageStrategy extends IStorageStrategy {
    private readonly inner = new LocalStorageStrategy();

    getAll(cacheKey: string): ICachePair<any>[] {
        try {
            return this.inner.getAll(cacheKey);
        } catch {
            // Corrupted CACHE_STORAGE JSON — reset so subsequent reads and writes recover.
            localStorage.removeItem(GlobalCacheConfig.globalCacheKey);
            return [];
        }
    }

    add(entity: ICachePair<any>, cacheKey: string): void {
        this.guardWrite(() => this.inner.add(entity, cacheKey));
    }

    addMany(entities: ICachePair<any>[], cacheKey: string): void {
        this.guardWrite(() => this.inner.addMany(entities, cacheKey));
    }

    update(index: number, entity: ICachePair<any>, cacheKey: string): void {
        this.guardWrite(() => this.inner.update(index, entity, cacheKey));
    }

    updateAtIndex(index: number, entity: ICachePair<any>, cacheKey: string): void {
        this.guardWrite(() => this.inner.updateAtIndex(index, entity, cacheKey));
    }

    remove(index: number, entity: ICachePair<any>, cacheKey: string): void {
        this.guardWrite(() => this.inner.remove(index, entity, cacheKey));
    }

    removeAtIndex(index: number, cacheKey: string): void {
        this.guardWrite(() => this.inner.removeAtIndex(index, cacheKey));
    }

    removeAll(cacheKey: string): void {
        this.guardWrite(() => this.inner.removeAll(cacheKey));
    }

    private guardWrite(write: () => void): void {
        try {
            write();
        } catch {
            try {
                localStorage.removeItem(GlobalCacheConfig.globalCacheKey);
                write();
            } catch {
                // Even an empty cache cannot hold this entry — serve it uncached.
            }
        }
    }
}

/**
 * Removes specific entries from the shared ts-cacheable localStorage blob. Used at startup to
 * drop legacy oversized entries (e.g. location data cached with the district polygons) that
 * would otherwise hog the quota until logout.
 */
const purgeCachedEntries = (cacheKeys: string[]): void => {
    const raw = localStorage.getItem(GlobalCacheConfig.globalCacheKey);
    if (!raw) {
        return;
    }

    try {
        const allCachedData = JSON.parse(raw);
        cacheKeys.forEach((cacheKey) => delete allCachedData[cacheKey]);
        localStorage.setItem(GlobalCacheConfig.globalCacheKey, JSON.stringify(allCachedData));
    } catch {
        localStorage.removeItem(GlobalCacheConfig.globalCacheKey);
    }
};

export { purgeCachedEntries, QuotaSafeLocalStorageStrategy };
